import { useState, useEffect, useMemo } from 'react';
import { loginToContentSquare } from '../../utils/csq/csqAuth';
import { CsqApiClient } from '../../utils/csq/csqApiClient';
import { ENTITY_HANDLERS, deleteAllItems, deleteAllEntityTypes, listAllEntityTypes, createAllEntityTypes, assertSourceDatasourceExcluded } from '../../utils/csq/csqEntityHandlers';
import { buildMappingsCreateFromSourceOperations, runMappingsCreateFromSourceOperations, listPages } from '../../utils/csq/csqMappings';
import { listZonings, listZoningsWithDetails } from '../../utils/csq/csqZonings';
import { listSegments, listSegmentsWithQuery, createSegment } from '../../utils/csq/csqSegments';
import { listAllPropertiesWithFormula, createProperty } from '../../utils/csq/csqProperties';
import { listDashboards, createDashboard, createDashboardForChart, copyDashboardContent } from '../../utils/csq/csqDashboards';
import { ENTITY_TYPES, ACTIONS, ENTITY_TYPES_WITH_SOURCE_REPLICATION } from '../../utils/csq/csqConstants';
import CredentialsSection from './entities-tab-sections/credentials-section';
import ScopeSection from './entities-tab-sections/scope-section';
import ActionSection from './entities-tab-sections/action-section';
import RunSection from './entities-tab-sections/run-section';

// Fixed entity-type order for the per-type breakdown lines logged after a composite (or
// single-type) create/delete/resync run — see `logEntityTypeBreakdown` below.
const ALL_ENTITY_TYPES_ORDER = ENTITY_TYPES.filter((t) => t.value !== 'all').map((t) => t.value);

// Loud "---- TITLE ----" banner, blank-line-padded and always the same width, so the phases of a
// Preview/Resync (to-be-deleted, to-be-created, deletion, creation, errors) are visually
// unmistakable in a long log instead of blurring together.
const LOG_HEADER_WIDTH = 42;
function logSectionHeader(appendLogFn, name) {
  const title = ` ${name.toUpperCase()} `;
  const dashes = Math.max(4, LOG_HEADER_WIDTH - title.length);
  const left = Math.floor(dashes / 2);
  appendLogFn('');
  appendLogFn(`${'-'.repeat(left)}${title}${'-'.repeat(dashes - left)}`);
  appendLogFn('');
}

// Failures/warnings are only repeated in the closing summary when there are fewer than this many
// of them (per kind) — beyond that, the summary just points back at the log.
const ISSUE_DETAIL_LIMIT = 5;

// Closing "ERRORS & WARNINGS" section: repeats each failed/warned entity with its error right at
// the END of the log, so nobody has to scroll back through hundreds of lines to find it. Lines
// containing FAILED / WARNING are colored by RunSection. `phases` is
// [{ phase: 'deletion' | 'creation', failures, warnings }].
function logIssueSummary(appendLogFn, phases) {
  const collect = (kind) => phases.flatMap(({ phase, [kind]: issues }) => (issues || []).map((issue) => ({ ...issue, phase })));
  const failures = collect('failures');
  const warnings = collect('warnings');
  if (failures.length === 0 && warnings.length === 0) return;

  const describe = (label, issue) => {
    const item = issue.item ? ` ${issue.item.id} (${issue.item.name || '(unnamed)'})` : '';
    return `  ${label} (${issue.phase}) [${issue.scopeId}] ${issue.entityType}${item}: ${issue.error}`;
  };
  const emit = (label, issues) => {
    if (issues.length === 0) return;
    if (issues.length >= ISSUE_DETAIL_LIMIT) {
      appendLogFn(`  ${label}: ${issues.length} item(s) — too many to repeat here, search the log above for "${label}".`);
      return;
    }
    issues.forEach((issue) => appendLogFn(describe(label, issue)));
  };

  logSectionHeader(appendLogFn, 'errors & warnings');
  emit('FAILED', failures);
  emit('WARNING', warnings);
}

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// One line per entity type, e.g. "Mappings | created: 0 | warning: 0 | failed: 2 |" — so a failure
// buried in a long log is also visible at a glance in the final summary. `label` is 'created' or
// 'deleted'.
function logEntityTypeLine(appendLogFn, label, type, success, warning, failed) {
  appendLogFn(`${capitalize(type)} | ${label}: ${success} | warning: ${warning} | failed: ${failed} |`);
}

// Composite-run counterpart: derives each entity type's success/warning/failed counts from the
// {entityType: count} `successByType` map and the {entityType, ...} `warnings`/`failures` arrays
// returned by deleteAllEntityTypes/createAllEntityTypes, and logs one line per type that had any
// activity (types untouched by this run are omitted).
function logEntityTypeBreakdown(appendLogFn, label, successByType, warnings, failures) {
  ALL_ENTITY_TYPES_ORDER.forEach((type) => {
    const success = (successByType && successByType[type]) || 0;
    const warning = warnings.filter((w) => w.entityType === type).length;
    const failed = failures.filter((f) => f.entityType === type).length;
    if (success === 0 && warning === 0 && failed === 0) return;
    logEntityTypeLine(appendLogFn, label, type, success, warning, failed);
  });
}

// Builds the "create from source" operations plan for zonings: one new target zoning per
// source zoning, with its page resolved by NAME (page ids differ across datasources).
// Shared by the standalone "Create from Source" action and by "Resync".
async function buildZoningsCreateFromSourceOperations({ sourceClient, targetClient, sourceDatasourceId, targetDatasourceIds, log }) {
  log(`Listing zonings for source datasource ${sourceDatasourceId}...`);
  const sourceZonings = await listZoningsWithDetails(sourceClient, sourceDatasourceId);
  log(`  found ${sourceZonings.length} source zoning(s).`);

  log(`Listing pages for source datasource ${sourceDatasourceId}...`);
  const sourcePages = await listPages(sourceClient, sourceDatasourceId, { take: 1000 });
  const pageById = new Map(sourcePages.map((page) => [page.id, page]));

  const operations = [];
  const plannedNames = new Set();
  const collisions = [];

  for (const targetDatasourceId of targetDatasourceIds) {
    log(`Listing existing zonings on target datasource ${targetDatasourceId}...`);
    const existingTargetZonings = await listZonings(targetClient, targetDatasourceId);
    const existingNames = new Set(existingTargetZonings.map((z) => z.name));

    for (const zoning of sourceZonings) {
      const sourcePage = pageById.get(zoning.pageId);
      if (!sourcePage) {
        log(`  WARNING: source zoning "${zoning.name}" (${zoning.id}) has no resolvable page (pageId ${zoning.pageId}), skipping.`);
        continue;
      }
      const plannedName = zoning.name;
      if (existingNames.has(plannedName) || plannedNames.has(`${targetDatasourceId}:${plannedName}`)) {
        collisions.push(`${targetDatasourceId}: "${plannedName}"`);
        continue;
      }
      plannedNames.add(`${targetDatasourceId}:${plannedName}`);
      operations.push({
        targetDatasourceId,
        config: {
          name: plannedName,
          pageName: sourcePage.name,
          pageMappingName: sourcePage.mappingName,
          pageTarget: sourcePage.target,
          device: zoning.device,
          width: zoning.width,
          height: zoning.height,
          userAgent: zoning.userAgent,
        },
      });
    }
  }

  return { operations, collisions };
}

// Executes the operations plan built above via the existing zonings handler (resolves the page
// by name on the target and runs the full screenshot+zoning creation flow).
async function runZoningsCreateFromSourceOperations(client, handler, operations, log) {
  let successCount = 0;
  const failures = [];
  for (const op of operations) {
    try {
      const created = await handler.create(client, op.targetDatasourceId, op.config, log);
      successCount += 1;
      log(`  ${op.targetDatasourceId}: created "${op.config.name}" — zoning ${created?.zoning?.id}.`);
    } catch (err) {
      failures.push({ ...op, error: err.message });
      log(`  ${op.targetDatasourceId}: "${op.config.name}" FAILED — ${err.message}`);
    }
  }
  return { successCount, failures };
}

// Builds the "create from source" operations plan for segments: one new target segment per
// source segment, carrying its full query definition (as opposed to the generic manual-JSON
// "create" flow, which always starts from a hardcoded blank template). Shared by the standalone
// "Create from Source" action and by "Resync".
async function buildSegmentsCreateFromSourceOperations({ sourceClient, targetClient, sourceDatasourceId, targetDatasourceIds, log }) {
  log(`Listing segments for source datasource ${sourceDatasourceId}...`);
  const sourceSegments = await listSegmentsWithQuery(sourceClient, sourceDatasourceId);
  log(`  found ${sourceSegments.length} source segment(s).`);

  const operations = [];
  const plannedNames = new Set();
  const collisions = [];

  for (const targetDatasourceId of targetDatasourceIds) {
    log(`Listing existing segments on target datasource ${targetDatasourceId}...`);
    const existingTargetSegments = await listSegments(targetClient, targetDatasourceId, { take: 1000 });
    const existingNames = new Set(existingTargetSegments.map((s) => s.name));

    for (const segment of sourceSegments) {
      const plannedName = segment.name;
      if (existingNames.has(plannedName) || plannedNames.has(`${targetDatasourceId}:${plannedName}`)) {
        collisions.push(`${targetDatasourceId}: "${plannedName}"`);
        continue;
      }
      plannedNames.add(`${targetDatasourceId}:${plannedName}`);
      operations.push({ targetDatasourceId, config: { name: plannedName, query: segment.query } });
    }
  }

  return { operations, collisions };
}

// Executes the operations plan built above via the segments handler.
async function runSegmentsCreateFromSourceOperations(client, operations, log) {
  let successCount = 0;
  const failures = [];
  for (const op of operations) {
    try {
      const created = await createSegment(client, op.targetDatasourceId, op.config);
      successCount += 1;
      log(`  ${op.targetDatasourceId}: created "${op.config.name}" (segment ${created && created.id}).`);
    } catch (err) {
      failures.push({ ...op, error: err.message });
      log(`  ${op.targetDatasourceId}: "${op.config.name}" FAILED — ${err.message}`);
    }
  }
  return { successCount, failures };
}

// Builds the "create from source" operations plan for custom properties: one new target property
// per source property (both "event" and "user" levels), carrying its full formula definition
// (as opposed to the generic manual-JSON "create" flow, which always starts from a hardcoded
// example formula). Shared by the standalone "Create from Source" action and by "Resync".
async function buildPropertiesCreateFromSourceOperations({ sourceClient, targetClient, sourceDatasourceId, targetDatasourceIds, log }) {
  log(`Listing custom properties for source datasource ${sourceDatasourceId}...`);
  const sourceProperties = await listAllPropertiesWithFormula(sourceClient, sourceDatasourceId);
  log(`  found ${sourceProperties.length} source custom propert${sourceProperties.length === 1 ? 'y' : 'ies'}.`);

  const operations = [];
  const plannedNames = new Set();
  const collisions = [];

  for (const targetDatasourceId of targetDatasourceIds) {
    log(`Listing existing custom properties on target datasource ${targetDatasourceId}...`);
    const existingTargetProperties = await listAllPropertiesWithFormula(targetClient, targetDatasourceId);
    const existingNames = new Set(existingTargetProperties.map((p) => p.name));

    for (const property of sourceProperties) {
      const plannedName = property.name;
      if (existingNames.has(plannedName) || plannedNames.has(`${targetDatasourceId}:${plannedName}`)) {
        collisions.push(`${targetDatasourceId}: "${plannedName}"`);
        continue;
      }
      plannedNames.add(`${targetDatasourceId}:${plannedName}`);
      operations.push({
        targetDatasourceId,
        config: { name: plannedName, level: property.level, formula: property.formula, notes: property.notes },
      });
    }
  }

  return { operations, collisions };
}

// Executes the operations plan built above via the properties handler.
async function runPropertiesCreateFromSourceOperations(client, operations, log) {
  let successCount = 0;
  const failures = [];
  for (const op of operations) {
    try {
      const created = await createProperty(client, op.targetDatasourceId, op.config);
      successCount += 1;
      log(`  ${op.targetDatasourceId}: created "${op.config.name}" (property ${created && created.id}).`);
    } catch (err) {
      failures.push({ ...op, error: err.message });
      log(`  ${op.targetDatasourceId}: "${op.config.name}" FAILED — ${err.message}`);
    }
  }
  return { successCount, failures };
}

// Builds the "create from source" operations plan for dashboards: one new target dashboard per
// source dashboard, INCLUDING ContentSquare's built-in template dashboards (RUM, Error
// Monitoring, etc.). Carries each source dashboard's `version` ('v4' widget-based or 'v5'
// chart-based, see csqDashboards.listDashboards) through to the run step below, since the two
// generations need different recreation handling — not every dashboard uses the same
// widget-groups/widgets model. Project-level (single target project), unlike mappings/zonings above.
async function buildDashboardsCreateFromSourceOperations({ sourceClient, sourceProjectId, log }) {
  log(`Listing dashboards for source project ${sourceProjectId}...`);
  const sourceDashboardsList = await listDashboards(sourceClient, sourceProjectId);
  log(`  found ${sourceDashboardsList.length} source dashboard(s).`);
  return sourceDashboardsList.map((dashboard) => ({ sourceDashboardId: dashboard.id, name: dashboard.name, version: dashboard.version }));
}

// Executes the operations plan built above: creates a new target dashboard per source dashboard,
// then (for v4/widget-based dashboards only) copies its widget groups + widgets onto it (see
// csqDashboards.copyDashboardContent). A v5/chart-based source dashboard has no widget-groups/
// widgets of its own (those v2/v3 endpoints 500 for it), so it's recreated via v5 instead and the
// widget copy is skipped — this standalone Dashboards flow has no way to replicate its actual
// chart content, since Charts isn't a source-replicated entity type (see
// ENTITY_TYPES_WITH_SOURCE_REPLICATION in csqConstants.js); use "All entity types" to get
// chart-based dashboards recreated WITH their charts.
async function runDashboardsCreateFromSourceOperations(sourceClient, targetClient, { sourceProjectId, targetProjectId, operations, log }) {
  let successCount = 0;
  const failures = [];
  for (const op of operations) {
    log(`  "${op.name}"...`);
    try {
      const isChartDashboard = op.version === 'v5';
      const targetDashboard = isChartDashboard
        ? await createDashboardForChart(targetClient, targetProjectId, op.name)
        : await createDashboard(targetClient, targetProjectId, op.name);
      const targetDashboardId = targetDashboard && targetDashboard.id;
      if (!targetDashboardId) {
        throw new Error(`Unexpected create-dashboard response: ${JSON.stringify(targetDashboard)}`);
      }
      if (isChartDashboard) {
        successCount += 1;
        log(`    created dashboard ${targetDashboardId} (chart-based — content not copied here; use "All entity types" to include its charts).`);
        continue;
      }
      const { groupCount, widgetCount } = await copyDashboardContent(sourceClient, targetClient, {
        sourceProjectId,
        sourceDashboardId: op.sourceDashboardId,
        targetProjectId,
        targetDashboardId,
        log: (line) => log(`    ${line}`),
      });
      successCount += 1;
      log(`    created dashboard ${targetDashboardId} (${groupCount} group(s), ${widgetCount} widget(s)).`);
    } catch (err) {
      failures.push({ ...op, error: err.message });
      log(`  "${op.name}" FAILED — ${err.message}`);
    }
  }
  return { successCount, failures };
}

function EntitiesTab({ options, optionsLoading }) {
  // Account is selected first — it determines which datasources are even selectable below (both
  // Source and Target are scoped to this one account's projects; the two no longer support
  // different accounts, see accountFilteredDatasources).
  const [selectedAccountId, setSelectedAccountId] = useState('');

  const selectedAccount = useMemo(
    () => options.accounts.find((a) => String(a.id) === selectedAccountId) || null,
    [options.accounts, selectedAccountId],
  );

  // Only accounts with at least one target datasource linked are selectable — an account with no
  // target has nothing to run entity actions against, so offering it would just dead-end the flow.
  const accountsWithTargetDatasource = useMemo(
    () => options.accounts.filter((a) => options.datasources.some((d) => d.kind === 'target' && String(d.project?.account?.id) === String(a.id))),
    [options.accounts, options.datasources],
  );

  const accountFilteredDatasources = useMemo(() => {
    if (!selectedAccountId) return [];
    return options.datasources.filter((d) => String(d.project?.account?.id) === selectedAccountId);
  }, [options.datasources, selectedAccountId]);

  const sourceDatasources = useMemo(() => accountFilteredDatasources.filter((d) => d.kind === 'source'), [accountFilteredDatasources]);
  const targetDatasources = useMemo(() => accountFilteredDatasources.filter((d) => d.kind === 'target'), [accountFilteredDatasources]);

  // Source and Target authenticate fully independently, own email and password each — Source can
  // be pre-linked to a datasource under a DIFFERENT account than the one selected above (e.g. a
  // cross-account link set up before Accounts existed), so its email is resolved from ITS OWN
  // project's account below, not from the selected Account.
  const [sourceEmail, setSourceEmail] = useState('');
  const [sourcePassword, setSourcePassword] = useState('');
  const [sourceAuthenticating, setSourceAuthenticating] = useState(false);
  const [sourceAuthError, setSourceAuthError] = useState(null);

  const [targetEmail, setTargetEmail] = useState('');
  const [targetPassword, setTargetPassword] = useState('');
  const [targetAuthenticating, setTargetAuthenticating] = useState(false);
  const [targetAuthError, setTargetAuthError] = useState(null);

  // Source datasource (dropdown-only, populated from the predefined SOURCE datasources).
  const [sourceDatasourceId, setSourceDatasourceId] = useState('');

  // Target datasource (single-select dropdown, same pattern as Source datasource). Starts empty —
  // it's destructive to target, so the user must select it intentionally rather than inherit a
  // pre-picked default.
  const [targetDatasourceId, setTargetDatasourceId] = useState('');

  // Changing the account invalidates whichever datasources were selected under the previous one
  // (they're no longer in the filtered lists above).
  useEffect(() => {
    setTargetDatasourceId('');
    setSourceDatasourceId('');
  }, [selectedAccountId]);

  // Project is never chosen directly — the target datasource belongs to exactly one project, so
  // the project is derived from whichever target datasource is currently selected instead (avoids
  // the two ever getting out of sync). Empty selection => no project yet.
  const selectedProject = useMemo(() => {
    if (!targetDatasourceId) return null;
    const datasource = targetDatasources.find((d) => String(d.datasourceId) === targetDatasourceId);
    return datasource?.project || null;
  }, [targetDatasourceId, targetDatasources]);

  // Resolved against the FULL datasource list, not the account-filtered sourceDatasources — the
  // Source can be pre-linked to a datasource outside the currently selected account, and its
  // project (and, below, its account/login) still needs to resolve correctly even though it
  // wouldn't appear as a selectable option in that filtered list.
  const selectedSourceDatasourceRecord = useMemo(() => {
    if (!sourceDatasourceId) return null;
    return options.datasources.find((d) => String(d.datasourceId) === sourceDatasourceId) || null;
  }, [sourceDatasourceId, options.datasources]);

  // Every source datasource must also be linked to a project (see the Manage Options datasources
  // form), so project-level entity types (charts/dashboards) can be listed from Source too (see
  // canListSource/handleRunSourceList below).
  const selectedSourceProject = selectedSourceDatasourceRecord?.project || null;

  // Pre-fill source datasource from the target's linked config when target is selected. Falls
  // back to the first available option if the target has no pre-linked source.
  useEffect(() => {
    if (!targetDatasourceId || optionsLoading) return;
    const target = targetDatasources.find((d) => String(d.datasourceId) === targetDatasourceId);
    if (target?.sourceDatasource) {
      setSourceDatasourceId(String(target.sourceDatasource.datasourceId));
    } else if (sourceDatasources.length > 0) {
      setSourceDatasourceId(String(sourceDatasources[0].datasourceId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDatasourceId]);

  // Target is always scoped to the selected Account (targetDatasources is filtered by it), so its
  // email always comes from that Account's linked user.
  useEffect(() => {
    if (!selectedAccount?.user) return;
    setTargetEmail(selectedAccount.user.email);
  }, [selectedAccount]);

  // Source's email is independent — resolved from its OWN project's account, which may differ
  // from the selected Account (see selectedSourceDatasourceRecord above).
  useEffect(() => {
    const email = selectedSourceDatasourceRecord?.project?.account?.user?.email;
    if (email) setSourceEmail(email);
  }, [selectedSourceDatasourceRecord]);

  // "all" only supports create/delete (see availableActions below), so its default action must
  // follow the same invariant enforced on every entityType change further down
  // (`setAction(nextEntityType === 'all' ? 'delete' : 'list')`) — defaulting to 'list' here would
  // leave the Action select showing no valid option.
  const [entityType, setEntityType] = useState('all');
  const [action, setAction] = useState('delete');

  const selectedEntityType = ENTITY_TYPES.find((t) => t.value === entityType);
  const handler = ENTITY_HANDLERS[entityType];
  // "all" only supports create/delete/resync (mirrors create-all.js/delete-all.js — there's no
  // combined "list" across every entity type). Every other entity type supports every action.
  const availableActions = entityType === 'all'
    ? ACTIONS.filter((a) => a.value === 'create' || a.value === 'delete' || a.value === 'resync')
    : ACTIONS;

  // Source only ever supports List (Create/Delete always mutate the Target). Every source
  // datasource must be linked to a project (see the Manage Options datasources form), so
  // project-level entity types (charts/dashboards) can be listed from Source too, resolved via
  // that linked project — same as datasource-level types use the source datasource id directly.
  const canListSource = entityType !== 'all';

  // Whether the selected entity type still uses the manual "Create payload" JSON editor — every
  // entity type in ENTITY_TYPES_WITH_SOURCE_REPLICATION (and "all") instead always replicates from
  // Source (see the `create-*-from-source` branches in handlePreview/handleConfirm below).
  const usesManualCreatePayload = entityType !== 'all' && !ENTITY_TYPES_WITH_SOURCE_REPLICATION.includes(entityType);

  // Resolved as actual numbers (not strings) — select/input values are always
  // strings in the DOM, but the API expects numeric project/datasource ids.
  const resolvedSourceDatasourceId = Number(sourceDatasourceId);
  const resolvedProjectId = selectedProject ? selectedProject.projectId : NaN;
  const resolvedTargetDatasourceId = Number(targetDatasourceId);
  // Wrapped in an array so the (potentially multi-target) list/create/delete handlers below and in
  // csqEntityHandlers.js don't need to change even though the UI now only ever selects one.
  const resolvedTargetDatasourceIds = targetDatasourceId ? [resolvedTargetDatasourceId] : [];

  // The id the selected entity type operates on (datasource-level entities use
  // the source datasource, project-level entities — charts/dashboards — use
  // the master project).
  const scopeId = selectedEntityType && selectedEntityType.level === 'project' ? resolvedProjectId : resolvedSourceDatasourceId;

  // Create-payload editor: pre-filled with the entity's hardcoded default
  // config (see defaultCreateConfig in csqEntityHandlers.js), but fully
  // editable — the user can see exactly what will be sent and tweak it before
  // running the action.
  const [configText, setConfigText] = useState('');
  const [configError, setConfigError] = useState(null);

  const resetConfigToDefault = () => {
    if (!handler || !usesManualCreatePayload) {
      setConfigText('');
      setConfigError(null);
      return;
    }
    setConfigText(JSON.stringify(handler.defaultCreateConfig(), null, 2));
    setConfigError(null);
  };

  // Reset the payload editor whenever the entity type changes or the user
  // switches to the "create" action, so it always starts from the hardcoded
  // default for the newly selected entity type.
  useEffect(() => {
    if (action === 'create') {
      resetConfigToDefault();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, action]);

  // Dry-run / execute plan state (Create/Delete/List — always against the Target).
  const [plan, setPlan] = useState(null);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const [runError, setRunError] = useState(null);

  // Source's List is wholly independent of the Target's action/plan above — its own login, its own
  // trigger, and its own result panel (see RunSection).
  const [sourceRun, setSourceRun] = useState({ error: null, lines: [] });

  const appendLog = (line) => setLog((lines) => [...lines, line]);

  const resetRun = () => {
    setPlan(null);
    setLog([]);
    setRunError(null);
    setSourceRun({ error: null, lines: [] });
  };

  // Logs in fresh every time (using the current email/password fields) — the
  // token is never cached/persisted, so every action re-authenticates.
  const ensureToken = async (side) => {
    const credentials = side === 'source' ? { email: sourceEmail, password: sourcePassword } : { email: targetEmail, password: targetPassword };
    const setAuthenticating = side === 'source' ? setSourceAuthenticating : setTargetAuthenticating;
    const setAuthError = side === 'source' ? setSourceAuthError : setTargetAuthError;
    setAuthenticating(true);
    setAuthError(null);
    try {
      const accessToken = await loginToContentSquare(credentials);
      return accessToken;
    } catch (err) {
      setAuthError(err.message);
      throw err;
    } finally {
      setAuthenticating(false);
    }
  };

  // "List" is read-only, so it runs immediately without a dry-run gate. For datasource-level entity
  // types this lists across every Target datasource (never the Source datasource, which has its own
  // independent List trigger below) — same scope as Create/Delete.
  const handleRunList = async () => {
    resetRun();
    if (selectedEntityType.level === 'project' && !selectedProject) {
      setRunError('Select a target datasource below to determine the project before listing this entity type.');
      return;
    }
    setRunning(true);
    try {
      const accessToken = await ensureToken('target');
      const client = new CsqApiClient({ token: accessToken });

      if (selectedEntityType.level === 'datasource') {
        if (resolvedTargetDatasourceIds.length === 0) {
          throw new Error(`No target datasources selected — add at least one before listing ${entityType}.`);
        }
        let total = 0;
        for (const datasourceId of resolvedTargetDatasourceIds) {
          appendLog(`Listing ${entityType} for datasource ${datasourceId}...`);
          const items = await handler.list(client, datasourceId);
          if (items.length === 0) {
            appendLog('  (none found)');
          } else {
            items.forEach((item) => appendLog(`  ${item.id}  ${item.name || '(unnamed)'}`));
          }
          total += items.length;
        }
        appendLog(`Done — ${total} item(s) across ${resolvedTargetDatasourceIds.length} target datasource(s).`);
        return;
      }

      appendLog(`Listing ${entityType} for ${selectedEntityType.level} ${scopeId}...`);
      const items = await handler.list(client, scopeId);
      if (items.length === 0) {
        appendLog('  (none found)');
      } else {
        items.forEach((item) => appendLog(`  ${item.id}  ${item.name || '(unnamed)'}`));
      }
      appendLog(`Done — ${items.length} item(s).`);
    } catch (err) {
      setRunError(err.message);
    } finally {
      setRunning(false);
    }
  };

  // Source only ever supports List — wholly independent of the Target's action/plan above, with its
  // own login and its own result panel (see RunSection). Datasource-level entity types list
  // straight off the source datasource id; project-level entity types (charts/dashboards) list off
  // the project linked to that source datasource instead (see canListSource/selectedSourceProject).
  const handleRunSourceList = async () => {
    setSourceRun({ error: null, lines: [] });
    const push = (line) => setSourceRun((prev) => ({ ...prev, lines: [...prev.lines, line] }));
    if (selectedEntityType.level === 'project' && !selectedSourceProject) {
      setSourceRun({ error: 'The selected source datasource has no linked project — link one in Manage Options before listing this entity type.', lines: [] });
      return;
    }
    const sourceScopeId = selectedEntityType.level === 'project' ? selectedSourceProject.projectId : resolvedSourceDatasourceId;
    try {
      const accessToken = await ensureToken('source');
      const client = new CsqApiClient({ token: accessToken });
      push(`Listing ${entityType} for source ${selectedEntityType.level} ${sourceScopeId}...`);
      const items = await handler.list(client, sourceScopeId);
      if (items.length === 0) {
        push('  (none found)');
      } else {
        items.forEach((item) => push(`  ${item.id}  ${item.name || '(unnamed)'}`));
      }
      push(`Done — ${items.length} item(s).`);
    } catch (err) {
      setSourceRun((prev) => ({ ...prev, error: err.message }));
    }
  };

  // Builds a preview of what "create" / "delete" would do, without
  // making any changes yet. Requires an explicit Confirm before executing.
  const handlePreview = async () => {
    resetRun();

    // Mappings' "create" bypasses the generic manual-JSON flow below (still used as-is by
    // zonings/segments/properties) — it fetches every mapping+page from Source and recreates
    // each source mapping as ONE new Target mapping carrying all of its original pages (i.e. an
    // exact mapping+pages+page-groups replica, not one new mapping per page).
    if (entityType === 'mappings' && action === 'create') {
      if (resolvedTargetDatasourceIds.length === 0) {
        setRunError('No target datasources selected — add at least one before creating mappings.');
        return;
      }
      try {
        assertSourceDatasourceExcluded(resolvedTargetDatasourceIds, resolvedSourceDatasourceId);
      } catch (err) {
        setRunError(err.message);
        return;
      }

      setRunning(true);
      try {
        const sourceToken = await ensureToken('source');
        const sourceClient = new CsqApiClient({ token: sourceToken });
        const targetToken = await ensureToken('target');
        const targetClient = new CsqApiClient({ token: targetToken });

        const { operations, collisions } = await buildMappingsCreateFromSourceOperations({
          sourceClient,
          targetClient,
          sourceDatasourceId: resolvedSourceDatasourceId,
          targetDatasourceIds: resolvedTargetDatasourceIds,
          log: appendLog,
        });

        if (collisions.length > 0) {
          throw new Error(
            `${collisions.length} planned mapping name(s) already exist on the target (delete them first): ${collisions.join(', ')}`,
          );
        }
        if (operations.length === 0) {
          throw new Error('Source has no mappings with pages to create.');
        }

        const totalPages = operations.reduce((sum, op) => sum + op.pageGroups.length, 0);
        appendLog(`This will create ${operations.length} new mapping(s) (${totalPages} page(s) total) on ${resolvedTargetDatasourceIds.length} target datasource(s):`);
        operations.forEach((op) => appendLog(`  ${op.targetDatasourceId}: "${op.mappingData.name}" (${op.pageGroups.length} page(s))`));
        setPlan({ type: 'create-mappings-from-source', operations });
      } catch (err) {
        setRunError(err.message);
      } finally {
        setRunning(false);
      }
      return;
    }

    // Zonings' "create" also bypasses the generic manual-JSON flow below — it fetches every
    // zoning (heatmap) from Source, resolves each one's page by NAME on Target (page ids differ
    // per datasource), and reruns the existing zoning-creation flow (screenshot + zoning) against
    // Target's own traffic — screenshots can't be copied as-is, they're generated fresh per
    // datasource, so this rebuilds each zoning's config (name/device/width/height/userAgent) and
    // lets `ENTITY_HANDLERS.zonings.create` do the real work, same as the manual path does today.
    if (entityType === 'zonings' && action === 'create') {
      if (resolvedTargetDatasourceIds.length === 0) {
        setRunError('No target datasources selected — add at least one before creating zonings.');
        return;
      }
      try {
        assertSourceDatasourceExcluded(resolvedTargetDatasourceIds, resolvedSourceDatasourceId);
      } catch (err) {
        setRunError(err.message);
        return;
      }

      setRunning(true);
      try {
        const sourceToken = await ensureToken('source');
        const sourceClient = new CsqApiClient({ token: sourceToken });
        const targetToken = await ensureToken('target');
        const targetClient = new CsqApiClient({ token: targetToken });

        const { operations, collisions } = await buildZoningsCreateFromSourceOperations({
          sourceClient,
          targetClient,
          sourceDatasourceId: resolvedSourceDatasourceId,
          targetDatasourceIds: resolvedTargetDatasourceIds,
          log: appendLog,
        });

        if (collisions.length > 0) {
          throw new Error(
            `${collisions.length} planned zoning name(s) already exist on the target (delete them first): ${collisions.join(', ')}`,
          );
        }
        if (operations.length === 0) {
          throw new Error('Source has no zonings with a resolvable page to create.');
        }

        appendLog(`This will create ${operations.length} new zoning(s) on ${resolvedTargetDatasourceIds.length} target datasource(s) (each takes a while — screenshots are generated fresh from Target's own traffic):`);
        operations.forEach((op) => appendLog(`  ${op.targetDatasourceId}: "${op.config.name}" (page "${op.config.pageName}")`));
        setPlan({ type: 'create-zonings-from-source', operations });
      } catch (err) {
        setRunError(err.message);
      } finally {
        setRunning(false);
      }
      return;
    }

    // Segments' "create" also bypasses the generic manual-JSON flow below — it fetches every
    // segment (with its full query) from Source and recreates each one as-is on Target, instead
    // of one blank default segment.
    if (entityType === 'segments' && action === 'create') {
      if (resolvedTargetDatasourceIds.length === 0) {
        setRunError('No target datasources selected — add at least one before creating segments.');
        return;
      }
      try {
        assertSourceDatasourceExcluded(resolvedTargetDatasourceIds, resolvedSourceDatasourceId);
      } catch (err) {
        setRunError(err.message);
        return;
      }

      setRunning(true);
      try {
        const sourceToken = await ensureToken('source');
        const sourceClient = new CsqApiClient({ token: sourceToken });
        const targetToken = await ensureToken('target');
        const targetClient = new CsqApiClient({ token: targetToken });

        const { operations, collisions } = await buildSegmentsCreateFromSourceOperations({
          sourceClient,
          targetClient,
          sourceDatasourceId: resolvedSourceDatasourceId,
          targetDatasourceIds: resolvedTargetDatasourceIds,
          log: appendLog,
        });

        if (collisions.length > 0) {
          throw new Error(
            `${collisions.length} planned segment name(s) already exist on the target (delete them first): ${collisions.join(', ')}`,
          );
        }
        if (operations.length === 0) {
          throw new Error('Source has no segments to create.');
        }

        appendLog(`This will create ${operations.length} new segment(s) on ${resolvedTargetDatasourceIds.length} target datasource(s):`);
        operations.forEach((op) => appendLog(`  ${op.targetDatasourceId}: "${op.config.name}"`));
        setPlan({ type: 'create-segments-from-source', operations });
      } catch (err) {
        setRunError(err.message);
      } finally {
        setRunning(false);
      }
      return;
    }

    // Custom properties' "create" also bypasses the generic manual-JSON flow below — it fetches
    // every property (event + user level, with its full formula) from Source and recreates each
    // one as-is on Target, instead of one example default property.
    if (entityType === 'properties' && action === 'create') {
      if (resolvedTargetDatasourceIds.length === 0) {
        setRunError('No target datasources selected — add at least one before creating custom properties.');
        return;
      }
      try {
        assertSourceDatasourceExcluded(resolvedTargetDatasourceIds, resolvedSourceDatasourceId);
      } catch (err) {
        setRunError(err.message);
        return;
      }

      setRunning(true);
      try {
        const sourceToken = await ensureToken('source');
        const sourceClient = new CsqApiClient({ token: sourceToken });
        const targetToken = await ensureToken('target');
        const targetClient = new CsqApiClient({ token: targetToken });

        const { operations, collisions } = await buildPropertiesCreateFromSourceOperations({
          sourceClient,
          targetClient,
          sourceDatasourceId: resolvedSourceDatasourceId,
          targetDatasourceIds: resolvedTargetDatasourceIds,
          log: appendLog,
        });

        if (collisions.length > 0) {
          throw new Error(
            `${collisions.length} planned custom property name(s) already exist on the target (delete them first): ${collisions.join(', ')}`,
          );
        }
        if (operations.length === 0) {
          throw new Error('Source has no custom properties to create.');
        }

        appendLog(`This will create ${operations.length} new custom propert${operations.length === 1 ? 'y' : 'ies'} on ${resolvedTargetDatasourceIds.length} target datasource(s):`);
        operations.forEach((op) => appendLog(`  ${op.targetDatasourceId}: "${op.config.name}" (${op.config.level})`));
        setPlan({ type: 'create-properties-from-source', operations });
      } catch (err) {
        setRunError(err.message);
      } finally {
        setRunning(false);
      }
      return;
    }

    // Dashboards' "create" also bypasses the generic manual-JSON flow below — it recreates every
    // dashboard from the source project (built-in template dashboards included), each with a full
    // copy of its source widget groups/widgets, instead of one empty default dashboard.
    if (entityType === 'dashboards' && action === 'create') {
      if (!selectedProject) {
        setRunError('Select a target datasource below to determine the project before creating dashboards.');
        return;
      }
      if (!selectedSourceProject) {
        setRunError('Source datasource must be linked to a project (see Manage Options) — dashboards are recreated from the source project.');
        return;
      }

      setRunning(true);
      try {
        const sourceToken = await ensureToken('source');
        const sourceClient = new CsqApiClient({ token: sourceToken });

        const operations = await buildDashboardsCreateFromSourceOperations({
          sourceClient,
          sourceProjectId: selectedSourceProject.projectId,
          log: appendLog,
        });

        if (operations.length === 0) {
          throw new Error('Source project has no dashboards to create.');
        }

        appendLog(`This will create ${operations.length} new dashboard(s) on project ${resolvedProjectId}, each with a full copy of its source widget groups/widgets:`);
        operations.forEach((op) => appendLog(`  "${op.name}"`));
        setPlan({ type: 'create-dashboards-from-source', operations });
      } catch (err) {
        setRunError(err.message);
      } finally {
        setRunning(false);
      }
      return;
    }

    if ((entityType === 'all' || selectedEntityType.level === 'project') && !selectedProject) {
      setRunError('Select a target datasource below to determine the project before running this action.');
      return;
    }

    // Composite "all" entity type: create-all/delete-all across every entity
    // type (mirrors sales-demo-env-setup's create-all.js/delete-all.js).
    if (entityType === 'all') {
      if ((action === 'create' || action === 'resync') && !selectedSourceProject) {
        setRunError('Source datasource must be linked to a project (see Manage Options) — dashboards are recreated from the source project.');
        return;
      }

      if (action === 'create') {
        // create-all doesn't take an editable payload — it's a fixed recipe
        // (mappings/zonings/segments/properties replicated per-target from source ->
        // replicate charts and recreate dashboards from source), so the preview is just a static
        // summary, no API calls.
        appendLog(`This will replicate mappings from source datasource ${resolvedSourceDatasourceId} into ${resolvedTargetDatasourceIds.length} target datasource(s):`);
        resolvedTargetDatasourceIds.forEach((id) => appendLog(`  ${id} — replicate mappings, zonings, segments, and custom properties from source`));
        appendLog(`Then replicate every chart and recreate every dashboard from source project ${selectedSourceProject.projectId} onto project ${resolvedProjectId}.`);
        setPlan({ type: 'create-all' });
        return;
      }

      setRunning(true);
      try {
        const accessToken = await ensureToken('target');
        const client = new CsqApiClient({ token: accessToken });

        logSectionHeader(appendLog, 'to be deleted on target');
        appendLog(`From target — listing what would be deleted (datasource(s) ${resolvedTargetDatasourceIds.join(', ')}, project ${resolvedProjectId}):`);
        const deletePreview = await deleteAllEntityTypes(client, {
          targetDatasourceIds: resolvedTargetDatasourceIds,
          projectId: resolvedProjectId,
          sourceDatasourceId: resolvedSourceDatasourceId,
          log: (line) => appendLog(`  ${line}`),
          dryRun: true,
        });
        appendLog(`Found ${deletePreview.found} item(s) total on target that would be deleted (${deletePreview.warnings.length} listing warning(s)).`);

        // 'delete' stops after deleting; 'resync' (Resync) also recreates everything from source
        // afterwards — see the `resync-all` handleConfirm branch below. Preview that recreate step
        // too, by listing (read-only) what create-all would find on Source — using a SOURCE-
        // authenticated client (source and target can be different CSQ accounts, so the target's
        // client may have no access to the source project at all).
        if (action === 'resync') {
          logSectionHeader(appendLog, 'to be created from source');
          appendLog(`From source (datasource ${resolvedSourceDatasourceId}, project ${selectedSourceProject.projectId}) — listing what would be (re)created on target:`);
          const sourceToken = await ensureToken('source');
          const sourceClient = new CsqApiClient({ token: sourceToken });
          const createPreview = await listAllEntityTypes(sourceClient, {
            datasourceIds: [resolvedSourceDatasourceId],
            projectId: selectedSourceProject.projectId,
            log: (line) => appendLog(`  ${line}`),
          });
          appendLog(`Found ${createPreview.found} item(s) total on source that would be (re)created on target (${createPreview.warnings.length} listing warning(s)).`);
        }

        setPlan({ type: action === 'resync' ? 'resync-all' : 'delete-all' });
      } catch (err) {
        setRunError(err.message);
      } finally {
        setRunning(false);
      }
      return;
    }

    if (action === 'create') {
      let parsedConfig;
      try {
        parsedConfig = JSON.parse(configText);
        setConfigError(null);
      } catch (err) {
        setConfigError(`Invalid JSON: ${err.message}`);
        return;
      }
      setPlan({ type: 'create', config: parsedConfig });
      return;
    }

    setRunning(true);
    try {
      const accessToken = await ensureToken('target');
      const client = new CsqApiClient({ token: accessToken });

      if (action === 'delete') {
        if (selectedEntityType.level === 'datasource') {
          assertSourceDatasourceExcluded(resolvedTargetDatasourceIds, resolvedSourceDatasourceId);
          if (resolvedTargetDatasourceIds.length === 0) {
            throw new Error(`No target datasources selected — add at least one before deleting ${entityType}.`);
          }
          appendLog(`Listing existing ${entityType} across ${resolvedTargetDatasourceIds.length} target datasource(s)...`);
          const items = [];
          for (const datasourceId of resolvedTargetDatasourceIds) {
            const datasourceItems = await handler.list(client, datasourceId);
            appendLog(`  ${datasourceId}: found ${datasourceItems.length} item(s).`);
            datasourceItems.forEach((item) => items.push({ ...item, __datasourceId: datasourceId }));
          }
          appendLog(`Found ${items.length} item(s) total that would be deleted.`);
          setPlan({ type: 'delete', items });
          return;
        }
        appendLog(`Listing existing ${entityType} for ${selectedEntityType.level} ${scopeId}...`);
        const items = await handler.list(client, scopeId);
        appendLog(`  found ${items.length} item(s) that would be deleted.`);
        setPlan({ type: 'delete', items });
        return;
      }

      // Resync = delete every existing item of this type on the target, then recreate it
      // (mappings/zonings/dashboards recreate from Source; everything else recreates from its
      // default payload) — see the `resync` handleConfirm branch below. The preview here only
      // lists what would be deleted, same as plain "Delete" — the recreation step isn't previewed.
      if (action === 'resync') {
        if (entityType === 'dashboards' && !selectedSourceProject) {
          throw new Error('Source datasource must be linked to a project (see Manage Options) — dashboards are recreated from the source project.');
        }
        if (selectedEntityType.level === 'datasource') {
          assertSourceDatasourceExcluded(resolvedTargetDatasourceIds, resolvedSourceDatasourceId);
          if (resolvedTargetDatasourceIds.length === 0) {
            throw new Error(`No target datasources selected — add at least one before resyncing ${entityType}.`);
          }
          appendLog(`Listing existing ${entityType} across ${resolvedTargetDatasourceIds.length} target datasource(s) (will be deleted, then recreated)...`);
          const items = [];
          for (const datasourceId of resolvedTargetDatasourceIds) {
            const datasourceItems = await handler.list(client, datasourceId);
            appendLog(`  ${datasourceId}: found ${datasourceItems.length} item(s).`);
            datasourceItems.forEach((item) => items.push({ ...item, __datasourceId: datasourceId }));
          }
          appendLog(`Found ${items.length} item(s) total that would be deleted, then recreated.`);
          setPlan({ type: 'resync', items });
          return;
        }
        appendLog(`Listing existing ${entityType} for ${selectedEntityType.level} ${scopeId} (will be deleted, then recreated)...`);
        const items = await handler.list(client, scopeId);
        appendLog(`  found ${items.length} item(s) that would be deleted, then recreated.`);
        setPlan({ type: 'resync', items });
        return;
      }
    } catch (err) {
      setRunError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleConfirm = async () => {
    if (!plan) return;
    setRunning(true);
    setRunError(null);
    try {
      const accessToken = await ensureToken('target');
      const client = new CsqApiClient({ token: accessToken });

      if (plan.type === 'create-all') {
        logSectionHeader(appendLog, 'creation');
        const sourceToken = await ensureToken('source');
        const sourceClient = new CsqApiClient({ token: sourceToken });
        const result = await createAllEntityTypes(sourceClient, client, {
          sourceDatasourceId: resolvedSourceDatasourceId,
          targetDatasourceIds: resolvedTargetDatasourceIds,
          projectId: resolvedProjectId,
          sourceProjectId: selectedSourceProject.projectId,
          log: appendLog,
        });
        appendLog(`Done — ${result.failures.length} failure(s), ${result.warnings.length} warning(s).`);
        logEntityTypeBreakdown(appendLog, 'created', result.successByType, result.warnings, result.failures);
        logIssueSummary(appendLog, [{ phase: 'creation', failures: result.failures, warnings: result.warnings }]);
      } else if (plan.type === 'delete-all') {
        logSectionHeader(appendLog, 'deletion');
        const result = await deleteAllEntityTypes(client, {
          targetDatasourceIds: resolvedTargetDatasourceIds,
          projectId: resolvedProjectId,
          sourceDatasourceId: resolvedSourceDatasourceId,
          log: appendLog,
          dryRun: false,
        });
        appendLog(`Done — deleted ${result.deleted}/${result.found}, failed ${result.failed}, ${result.warnings.length} warning(s).`);
        logEntityTypeBreakdown(appendLog, 'deleted', result.successByType, result.warnings, result.failures);
        logIssueSummary(appendLog, [{ phase: 'deletion', failures: result.failures, warnings: result.warnings }]);
      } else if (plan.type === 'resync-all') {
        logSectionHeader(appendLog, 'deletion');
        const deleteResult = await deleteAllEntityTypes(client, {
          targetDatasourceIds: resolvedTargetDatasourceIds,
          projectId: resolvedProjectId,
          sourceDatasourceId: resolvedSourceDatasourceId,
          log: appendLog,
          dryRun: false,
        });
        appendLog(`Deleted ${deleteResult.deleted}/${deleteResult.found}, failed ${deleteResult.failed}, ${deleteResult.warnings.length} warning(s).`);
        logEntityTypeBreakdown(appendLog, 'deleted', deleteResult.successByType, deleteResult.warnings, deleteResult.failures);
        logSectionHeader(appendLog, 'creation');
        try {
          const sourceToken = await ensureToken('source');
          const sourceClient = new CsqApiClient({ token: sourceToken });
          const createResult = await createAllEntityTypes(sourceClient, client, {
            sourceDatasourceId: resolvedSourceDatasourceId,
            targetDatasourceIds: resolvedTargetDatasourceIds,
            projectId: resolvedProjectId,
            sourceProjectId: selectedSourceProject.projectId,
            log: appendLog,
          });
          appendLog(`Done — ${createResult.failures.length} failure(s), ${createResult.warnings.length} warning(s).`);
          logEntityTypeBreakdown(appendLog, 'created', createResult.successByType, createResult.warnings, createResult.failures);
          if (createResult.failures.length > 0 || createResult.warnings.length > 0) {
            appendLog(`WARNING: Target was fully cleared, but recreation had ${createResult.failures.length} failure(s) and ${createResult.warnings.length} warning(s) — Target may now be missing some entities. Re-run Resync (or Create) to finish.`);
          }
          logIssueSummary(appendLog, [
            { phase: 'deletion', failures: deleteResult.failures, warnings: deleteResult.warnings },
            { phase: 'creation', failures: createResult.failures, warnings: createResult.warnings },
          ]);
        } catch (err) {
          appendLog(`WARNING: Target was fully cleared, but recreation failed before finishing (${err.message}) — Target may now be missing entities. Re-run Resync (or Create) to finish.`);
          throw err;
        }
      } else if (plan.type === 'create-mappings-from-source') {
        appendLog(`Creating ${plan.operations.length} mapping(s) from source...`);
        const result = await runMappingsCreateFromSourceOperations(client, plan.operations, appendLog);
        appendLog(`Done — created ${result.successCount}/${plan.operations.length}, ${result.failures.length} failure(s).`);
      } else if (plan.type === 'create-zonings-from-source') {
        appendLog(`Creating ${plan.operations.length} zoning(s) from source...`);
        const result = await runZoningsCreateFromSourceOperations(client, handler, plan.operations, appendLog);
        appendLog(`Done — created ${result.successCount}/${plan.operations.length}, ${result.failures.length} failure(s).`);
      } else if (plan.type === 'create-segments-from-source') {
        appendLog(`Creating ${plan.operations.length} segment(s) from source...`);
        const result = await runSegmentsCreateFromSourceOperations(client, plan.operations, appendLog);
        appendLog(`Done — created ${result.successCount}/${plan.operations.length}, ${result.failures.length} failure(s).`);
      } else if (plan.type === 'create-properties-from-source') {
        appendLog(`Creating ${plan.operations.length} custom propert${plan.operations.length === 1 ? 'y' : 'ies'} from source...`);
        const result = await runPropertiesCreateFromSourceOperations(client, plan.operations, appendLog);
        appendLog(`Done — created ${result.successCount}/${plan.operations.length}, ${result.failures.length} failure(s).`);
      } else if (plan.type === 'create-dashboards-from-source') {
        appendLog(`Creating ${plan.operations.length} dashboard(s) from source...`);
        const sourceToken = await ensureToken('source');
        const sourceClient = new CsqApiClient({ token: sourceToken });
        const result = await runDashboardsCreateFromSourceOperations(sourceClient, client, {
          sourceProjectId: selectedSourceProject.projectId,
          targetProjectId: resolvedProjectId,
          operations: plan.operations,
          log: appendLog,
        });
        appendLog(`Done — created ${result.successCount}/${plan.operations.length}, ${result.failures.length} failure(s).`);
      } else if (plan.type === 'create') {
        appendLog(`Creating ${entityType} for ${selectedEntityType.level} ${scopeId}...`);
        const created = await handler.create(client, scopeId, plan.config, appendLog);
        appendLog(`Done — created: ${JSON.stringify(created)}`);
      } else if (plan.type === 'delete') {
        appendLog(`Deleting ${plan.items.length} ${entityType} item(s)...`);
        if (selectedEntityType.level === 'datasource') {
          const byDatasource = new Map();
          plan.items.forEach((item) => {
            const group = byDatasource.get(item.__datasourceId) || [];
            group.push(item);
            byDatasource.set(item.__datasourceId, group);
          });
          let found = 0;
          let deleted = 0;
          let failed = 0;
          for (const [datasourceId, items] of byDatasource) {
            const result = await deleteAllItems(client, handler, datasourceId, items, appendLog);
            found += result.found;
            deleted += result.deleted;
            failed += result.failed;
          }
          appendLog(`Done — deleted ${deleted}/${found}, failed ${failed}.`);
        } else {
          const result = await deleteAllItems(client, handler, scopeId, plan.items, appendLog);
          appendLog(`Done — deleted ${result.deleted}/${result.found}, failed ${result.failed}.`);
        }
      } else if (plan.type === 'resync') {
        // Resync = delete every existing item of this type on the target, then recreate it —
        // mappings/zonings recreate from Source (same building blocks as their standalone "Create
        // from Source" flows); everything else recreates using its default payload, since there's
        // no per-source replication built for those entity types yet. This is a single confirm
        // step — if recreation fails partway through, Target is left with whatever finished
        // deleting/recreating so far (a warning is logged below); re-running Resync (or Create)
        // finishes the job.
        logSectionHeader(appendLog, 'deletion');
        appendLog(`Resyncing ${plan.items.length} ${entityType} item(s): deleting first...`);
        if (selectedEntityType.level === 'datasource') {
          const byDatasource = new Map();
          plan.items.forEach((item) => {
            const group = byDatasource.get(item.__datasourceId) || [];
            group.push(item);
            byDatasource.set(item.__datasourceId, group);
          });
          let found = 0;
          let deleted = 0;
          let failed = 0;
          for (const [datasourceId, items] of byDatasource) {
            const result = await deleteAllItems(client, handler, datasourceId, items, appendLog);
            found += result.found;
            deleted += result.deleted;
            failed += result.failed;
          }
          appendLog(`Deleted ${deleted}/${found}, failed ${failed}.`);
          logEntityTypeLine(appendLog, 'deleted', entityType, deleted, 0, failed);
        } else {
          const result = await deleteAllItems(client, handler, scopeId, plan.items, appendLog);
          appendLog(`Deleted ${result.deleted}/${result.found}, failed ${result.failed}.`);
          logEntityTypeLine(appendLog, 'deleted', entityType, result.deleted, 0, result.failed);
        }

        logSectionHeader(appendLog, 'creation');
        appendLog(`Recreating ${entityType}...`);
        try {
          if (entityType === 'mappings' || entityType === 'zonings' || entityType === 'segments' || entityType === 'properties') {
            const sourceToken = await ensureToken('source');
            const sourceClient = new CsqApiClient({ token: sourceToken });

            if (entityType === 'mappings') {
              const { operations, collisions } = await buildMappingsCreateFromSourceOperations({
                sourceClient,
                targetClient: client,
                sourceDatasourceId: resolvedSourceDatasourceId,
                targetDatasourceIds: resolvedTargetDatasourceIds,
                log: appendLog,
              });
              if (collisions.length > 0) {
                appendLog(`  WARNING: ${collisions.length} name collision(s) survived the delete, skipping: ${collisions.join(', ')}`);
              }
              const result = await runMappingsCreateFromSourceOperations(client, operations, appendLog);
              appendLog(`Done — created ${result.successCount}/${operations.length}, ${result.failures.length} failure(s).`);
              logEntityTypeLine(appendLog, 'created', entityType, result.successCount, collisions.length, result.failures.length);
              if (result.failures.length > 0) {
                appendLog(`WARNING: Target's existing ${entityType} were deleted, but ${result.failures.length}/${operations.length} failed to recreate — Target now has fewer ${entityType} than Source. Re-run Resync (or Create) to finish.`);
              }
            } else if (entityType === 'zonings') {
              const { operations, collisions } = await buildZoningsCreateFromSourceOperations({
                sourceClient,
                targetClient: client,
                sourceDatasourceId: resolvedSourceDatasourceId,
                targetDatasourceIds: resolvedTargetDatasourceIds,
                log: appendLog,
              });
              if (collisions.length > 0) {
                appendLog(`  WARNING: ${collisions.length} name collision(s) survived the delete, skipping: ${collisions.join(', ')}`);
              }
              const result = await runZoningsCreateFromSourceOperations(client, handler, operations, appendLog);
              appendLog(`Done — created ${result.successCount}/${operations.length}, ${result.failures.length} failure(s).`);
              logEntityTypeLine(appendLog, 'created', entityType, result.successCount, collisions.length, result.failures.length);
              if (result.failures.length > 0) {
                appendLog(`WARNING: Target's existing ${entityType} were deleted, but ${result.failures.length}/${operations.length} failed to recreate — Target now has fewer ${entityType} than Source. Re-run Resync (or Create) to finish.`);
              }
            } else if (entityType === 'segments') {
              const { operations, collisions } = await buildSegmentsCreateFromSourceOperations({
                sourceClient,
                targetClient: client,
                sourceDatasourceId: resolvedSourceDatasourceId,
                targetDatasourceIds: resolvedTargetDatasourceIds,
                log: appendLog,
              });
              if (collisions.length > 0) {
                appendLog(`  WARNING: ${collisions.length} name collision(s) survived the delete, skipping: ${collisions.join(', ')}`);
              }
              const result = await runSegmentsCreateFromSourceOperations(client, operations, appendLog);
              appendLog(`Done — created ${result.successCount}/${operations.length}, ${result.failures.length} failure(s).`);
              logEntityTypeLine(appendLog, 'created', entityType, result.successCount, collisions.length, result.failures.length);
              if (result.failures.length > 0) {
                appendLog(`WARNING: Target's existing ${entityType} were deleted, but ${result.failures.length}/${operations.length} failed to recreate — Target now has fewer ${entityType} than Source. Re-run Resync (or Create) to finish.`);
              }
            } else {
              const { operations, collisions } = await buildPropertiesCreateFromSourceOperations({
                sourceClient,
                targetClient: client,
                sourceDatasourceId: resolvedSourceDatasourceId,
                targetDatasourceIds: resolvedTargetDatasourceIds,
                log: appendLog,
              });
              if (collisions.length > 0) {
                appendLog(`  WARNING: ${collisions.length} name collision(s) survived the delete, skipping: ${collisions.join(', ')}`);
              }
              const result = await runPropertiesCreateFromSourceOperations(client, operations, appendLog);
              appendLog(`Done — created ${result.successCount}/${operations.length}, ${result.failures.length} failure(s).`);
              logEntityTypeLine(appendLog, 'created', entityType, result.successCount, collisions.length, result.failures.length);
              if (result.failures.length > 0) {
                appendLog(`WARNING: Target's existing ${entityType} were deleted, but ${result.failures.length}/${operations.length} failed to recreate — Target now has fewer ${entityType} than Source. Re-run Resync (or Create) to finish.`);
              }
            }
          } else if (entityType === 'dashboards') {
            const sourceToken = await ensureToken('source');
            const sourceClient = new CsqApiClient({ token: sourceToken });
            const operations = await buildDashboardsCreateFromSourceOperations({
              sourceClient,
              sourceProjectId: selectedSourceProject.projectId,
              log: appendLog,
            });
            const result = await runDashboardsCreateFromSourceOperations(sourceClient, client, {
              sourceProjectId: selectedSourceProject.projectId,
              targetProjectId: resolvedProjectId,
              operations,
              log: appendLog,
            });
            appendLog(`Done — created ${result.successCount}/${operations.length}, ${result.failures.length} failure(s).`);
            logEntityTypeLine(appendLog, 'created', entityType, result.successCount, 0, result.failures.length);
            if (result.failures.length > 0) {
              appendLog(`WARNING: Target's existing ${entityType} were deleted, but ${result.failures.length}/${operations.length} failed to recreate — Target now has fewer ${entityType} than Source. Re-run Resync (or Create) to finish.`);
            }
          } else {
            const created = await handler.create(client, scopeId, handler.defaultCreateConfig(), appendLog);
            appendLog(`Done — created: ${JSON.stringify(created)}`);
            logEntityTypeLine(appendLog, 'created', entityType, 1, 0, 0);
          }
        } catch (err) {
          appendLog(`WARNING: Target's existing ${entityType} were deleted, but recreation failed before finishing (${err.message}) — Target may now be missing ${entityType}. Re-run Resync (or Create) to finish.`);
          throw err;
        }
      }
      setPlan(null);
    } catch (err) {
      setRunError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleCancelPlan = () => {
    setPlan(null);
  };

  return (
    <>
      <ScopeSection
        accounts={accountsWithTargetDatasource}
        selectedAccountId={selectedAccountId}
        setSelectedAccountId={setSelectedAccountId}
        allDatasources={options.datasources}
        sourceDatasources={sourceDatasources}
        targetDatasources={targetDatasources}
        sourceDatasourceId={sourceDatasourceId}
        targetDatasourceId={targetDatasourceId}
        setTargetDatasourceId={setTargetDatasourceId}
        resolvedSourceDatasourceId={resolvedSourceDatasourceId}
        resolvedTargetDatasourceId={resolvedTargetDatasourceId}
        selectedProject={selectedProject}
        selectedSourceProject={selectedSourceProject}
        resetRun={resetRun}
      />

      {targetDatasourceId && (
        <>
          <CredentialsSection
            sourceEmail={sourceEmail}
            sourcePassword={sourcePassword}
            setSourcePassword={setSourcePassword}
            sourceAuthenticating={sourceAuthenticating}
            sourceAuthError={sourceAuthError}
            targetEmail={targetEmail}
            targetPassword={targetPassword}
            setTargetPassword={setTargetPassword}
            targetAuthenticating={targetAuthenticating}
            targetAuthError={targetAuthError}
          />

          <ActionSection
            entityType={entityType}
            setEntityType={setEntityType}
            action={action}
            setAction={setAction}
            availableActions={availableActions}
            selectedEntityType={selectedEntityType}
            canListSource={canListSource}
            resetRun={resetRun}
            configText={configText}
            setConfigText={setConfigText}
            configError={configError}
            setConfigError={setConfigError}
            resetConfigToDefault={resetConfigToDefault}
          />

          <RunSection
            action={action}
            plan={plan}
            running={running}
            onRunList={handleRunList}
            onPreview={handlePreview}
            onConfirm={handleConfirm}
            onCancelPlan={handleCancelPlan}
            runError={runError}
            log={log}
            canListSource={canListSource}
            onRunSourceList={handleRunSourceList}
            sourceRun={sourceRun}
            resolvedSourceDatasourceId={resolvedSourceDatasourceId}
            resolvedTargetDatasourceId={resolvedTargetDatasourceId}
          />
        </>
      )}
    </>
  );
}

export default EntitiesTab;
